import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Edit3, ChevronDown, X, Trash2, Check
} from 'lucide-react';
import { getProducerName } from '../utils/userUtils';

const TagFolderManager = ({
  availableTags = [],
  currentFilter = 'All Tags',
  onFilterChange,
  onTagDeleteGlobally,
  onTagsChanged, // Callback when tags/folders are modified
  producer = 'All Producers', // Producer filter for loading producer-specific folders
  currentUser = null // Current logged-in user to determine edit permissions
}) => {
  const [contactFolders, setContactFolders] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedTag, setDraggedTag] = useState(null);
  const [dragSourceFolder, setDragSourceFolder] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [folderRenameState, setFolderRenameState] = useState({ id: null, value: '' });
  const [tagRenameState, setTagRenameState] = useState({ tag: null, value: '' });
  const [isSavingFolderRename, setIsSavingFolderRename] = useState(false);
  const [isSavingTagRename, setIsSavingTagRename] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const loadContactFolders = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (producer) {
        params.append('producer', producer);
      }

      const response = await axios.get(`/api/contact-management/folders?${params.toString()}`);
      if (response.data.success) {
        setContactFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Failed to load contact folders:', error);
    }
  }, [producer]);

  const createFolder = async (name) => {
    try {
      const response = await axios.post('/api/contact-management/folders', {
        name,
        include_tags: ''
      });
      if (response.data.success) {
        loadContactFolders();
        setNewFolderName('');
        if (onTagsChanged) onTagsChanged();
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert('Failed to create folder: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateFolderTags = async (folderId, includeTags) => {
    try {
      const response = await axios.put(`/api/contact-management/folders/${folderId}`, {
        include_tags: includeTags.join(',')
      });
      if (response.data.success) {
        loadContactFolders();
        if (onTagsChanged) onTagsChanged();
      }
    } catch (error) {
      console.error('Failed to update folder tags:', error);
    }
  };

  const moveTagBetweenFolders = async (tagName, targetFolderId, sourceFolderId = null) => {
    try {
      // If there's a source folder, remove the tag from it
      if (sourceFolderId) {
        const sourceFolder = contactFolders.find(f => f.id === sourceFolderId);
        if (sourceFolder && sourceFolder.include_tags) {
          const sourceTags = sourceFolder.include_tags.split(',').map(t => t.trim()).filter(t => t !== tagName);
          await updateFolderTags(sourceFolderId, sourceTags);
        }
      }

      // Add the tag to the target folder
      const targetFolder = contactFolders.find(f => f.id === targetFolderId);
      if (targetFolder) {
        const targetTags = targetFolder.include_tags ? targetFolder.include_tags.split(',').map(t => t.trim()).filter(t => t) : [];
        if (!targetTags.includes(tagName)) {
          await updateFolderTags(targetFolderId, [...targetTags, tagName]);
        }
      }
    } catch (error) {
      console.error('Failed to move tag between folders:', error);
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder? Tags will become unorganized.')) {
      return;
    }

    try {
      const response = await axios.delete(`/api/contact-management/folders/${folderId}`);
      if (response.data.success) {
        loadContactFolders();
        if (onTagsChanged) onTagsChanged();
      }
    } catch (error) {
      console.error('Failed to delete folder:', error);
    }
  };

  const startFolderRename = (folder) => {
    setFolderRenameState({ id: folder.id, value: folder.name });
  };

  const cancelFolderRename = () => {
    setFolderRenameState({ id: null, value: '' });
  };

  const submitFolderRename = async () => {
    if (!folderRenameState.id) return;

    const trimmedName = folderRenameState.value.trim();
    if (!trimmedName) {
      cancelFolderRename();
      return;
    }

    const existingFolder = contactFolders.find(f => f.id === folderRenameState.id);
    if (existingFolder && existingFolder.name === trimmedName) {
      cancelFolderRename();
      return;
    }

    try {
      setIsSavingFolderRename(true);
      await axios.put(`/api/contact-management/folders/${folderRenameState.id}`, {
        name: trimmedName
      });
      await loadContactFolders();
      if (onTagsChanged) onTagsChanged();
    } catch (error) {
      console.error('Failed to rename folder:', error);
      alert('Failed to rename folder: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSavingFolderRename(false);
      cancelFolderRename();
    }
  };

  const startTagRename = (tag) => {
    setTagRenameState({ tag, value: tag });
  };

  const cancelTagRename = () => {
    setTagRenameState({ tag: null, value: '' });
  };

  const submitTagRename = async () => {
    if (!tagRenameState.tag) return;

    const trimmedTag = tagRenameState.value.trim();
    if (!trimmedTag || trimmedTag === tagRenameState.tag) {
      cancelTagRename();
      return;
    }

    try {
      setIsSavingTagRename(true);
      await axios.put(`/api/contact-management/tags/${encodeURIComponent(tagRenameState.tag)}`, {
        newTagName: trimmedTag
      });
      await loadContactFolders();
      if (onTagsChanged) onTagsChanged();
      if (currentFilter === tagRenameState.tag && onFilterChange) {
        onFilterChange('tag', trimmedTag);
      }
    } catch (error) {
      console.error('Failed to rename tag:', error);
      alert('Failed to rename tag: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSavingTagRename(false);
      cancelTagRename();
    }
  };

  const handleRenameInputKeyDown = (event, submitFn, cancelFn) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitFn();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelFn();
    }
  };

  const getUnorganizedTags = () => {
    const organizedTags = new Set();
    contactFolders.forEach(folder => {
      if (folder.include_tags) {
        folder.include_tags.split(',').forEach(tag => organizedTags.add(tag.trim()));
      }
    });
    return availableTags.filter(tag => !organizedTags.has(tag));
  };

  const handleTagDelete = (tag) => {
    if (onTagDeleteGlobally) {
      onTagDeleteGlobally(tag);
    }
    if (onTagsChanged) onTagsChanged();
  };

  const handleFilterSelect = (tag) => {
    if (onFilterChange) {
      onFilterChange('tag', tag);
    }
    setIsDropdownOpen(false);
  };

  // Calculate dropdown position when opening
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Handle dropdown toggle with position calculation
  const handleDropdownToggle = () => {
    if (!isDropdownOpen) {
      updateDropdownPosition();
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Determine if current user can edit folders
  const canEditFolders = () => {
    if (!currentUser) return false;

    if (producer === 'All Producers') {
      return false;
    }

    return (producer == currentUser.id);
  };

  // Get display name for folder with producer info when needed
  const getFolderDisplayName = (folder) => {
    if (producer === 'All Producers' && folder.user_id) {
      // Create user object with data from backend to pass to getProducerName
      const userObj = {
        id: folder.user_id,
        displayName: folder.display_name,
        username: folder.username
      };
      const producerName = getProducerName(userObj);
      return `${folder.name} (${producerName})`;
    }
    return folder.name;
  };

  // Load folders on mount
  useEffect(() => {
    loadContactFolders();
  }, [loadContactFolders]);

  // Modal management
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isModalOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen &&
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Reposition dropdown on scroll/resize
  useEffect(() => {
    if (isDropdownOpen) {
      const handleReposition = () => {
        updateDropdownPosition();
      };

      window.addEventListener('scroll', handleReposition);
      window.addEventListener('resize', handleReposition);

      return () => {
        window.removeEventListener('scroll', handleReposition);
        window.removeEventListener('resize', handleReposition);
      };
    }
  }, [isDropdownOpen]);

  return (
    <>
      <div className="relative tag-dropdown">
        <button
          ref={buttonRef}
          onClick={handleDropdownToggle}
          className="p-2 border border-gray-300 rounded-lg bg-white flex items-center justify-between min-w-48"
        >
          <span>{currentFilter}</span>
          <div className="flex items-center gap-2">
            {canEditFolders() && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(false);
                  setIsModalOpen(true);
                }}
                className="text-gray-400 hover:text-blue-600 p-1 rounded"
                title="Edit folders and tags"
              >
                <Edit3 size={14} />
              </button>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {isDropdownOpen && (
          <div
            ref={dropdownRef}
            className="bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-y-auto min-w-80"
            style={{
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              minWidth: Math.max(dropdownPosition.width, 320),
              zIndex: 10000
            }}
          >
            {/* All Tags Option */}
            <div
              onClick={() => handleFilterSelect('All Tags')}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100"
            >
              <span className={`${currentFilter === 'All Tags' ? 'font-medium text-blue-600' : ''}`}>All Tags</span>
            </div>

            {/* Contact Folders */}
            {contactFolders.map(folder => (
              <div key={folder.id} className="border-b border-gray-100 last:border-b-0">
                <div className="px-3 py-2 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setExpandedFolders(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(folder.id)) {
                            newSet.delete(folder.id);
                          } else {
                            newSet.add(folder.id);
                          }
                          return newSet;
                        });
                      }}
                    >
                      <ChevronDown
                        size={14}
                        className={`transition-transform ${expandedFolders.has(folder.id) ? 'rotate-180' : 'rotate-[-90deg]'}`}
                      />
                    </button>
                    <span className="font-medium text-sm">📁 {getFolderDisplayName(folder)}</span>
                    {folder.include_tags && (
                      <span className="text-xs text-gray-500">
                        ({folder.include_tags.split(',').length} tags)
                      </span>
                    )}
                  </div>
                </div>

                {/* Folder Tags */}
                {expandedFolders.has(folder.id) && folder.include_tags && (
                  <div className="pl-8 pb-2">
                    {folder.include_tags.split(',').map(tag => {
                      const trimmedTag = tag.trim();
                      return (
                        <div
                          key={trimmedTag}
                          className="px-2 py-1 text-sm cursor-pointer hover:bg-blue-50"
                          onClick={() => handleFilterSelect(trimmedTag)}
                        >
                          <span className={`${currentFilter === trimmedTag ? 'font-medium text-blue-600' : ''}`}>
                            🏷️ {trimmedTag}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {/* Unorganized Tags */}
            {getUnorganizedTags().length > 0 && (
              <div className="border-t border-gray-200">
                <div className="px-3 py-2 bg-gray-50">
                  <span className="font-medium text-sm text-gray-600">📂 Unorganized Tags</span>
                </div>
                {getUnorganizedTags().map(tag => (
                  <div
                    key={tag}
                    className="px-3 py-1 text-sm cursor-pointer hover:bg-blue-50"
                    onClick={() => handleFilterSelect(tag)}
                  >
                    <span className={`${currentFilter === tag ? 'font-medium text-blue-600' : ''}`}>
                      🏷️ {tag}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Tag & Folder Manager</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-hidden p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                {/* Left Column - Folders */}
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-medium mb-4">📁 Tag Folders</h3>
                  <div className="flex-1 border rounded-lg overflow-hidden min-h-0">
                    <div className="h-full overflow-y-auto bg-gray-50 max-h-[50vh]">
                      {contactFolders.map(folder => (
                        <div key={folder.id} className="border-b border-gray-200 bg-white">
                          <div
                            className={`p-4 flex items-center justify-between group ${draggedTag ? 'border-2 border-dashed border-transparent hover:border-blue-400 hover:bg-blue-50' : ''}`}
                            onDragOver={(e) => {
                              if (draggedTag) {
                                e.preventDefault();
                                e.currentTarget.style.backgroundColor = '#dbeafe';
                              }
                            }}
                            onDragLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '';
                            }}
                            onDrop={(e) => {
                              if (draggedTag) {
                                e.preventDefault();
                                e.currentTarget.style.backgroundColor = '';

                                if (dragSourceFolder !== folder.id) {
                                  moveTagBetweenFolders(draggedTag, folder.id, dragSourceFolder);
                                }

                                setDraggedTag(null);
                                setDragSourceFolder(null);
                              }
                            }}
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <button
                                onClick={() => {
                                  setExpandedFolders(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(folder.id)) {
                                      newSet.delete(folder.id);
                                    } else {
                                      newSet.add(folder.id);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                <ChevronDown
                                  size={16}
                                  className={`transition-transform ${expandedFolders.has(folder.id) ? 'rotate-180' : 'rotate-[-90deg]'}`}
                                />
                              </button>
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                {folderRenameState.id === folder.id ? (
                                  <>
                                    <input
                                      type="text"
                                      value={folderRenameState.value}
                                      autoFocus
                                      onChange={(e) => setFolderRenameState(prev => ({
                                        ...prev,
                                        value: e.target.value
                                      }))}
                                      onKeyDown={(e) => handleRenameInputKeyDown(e, submitFolderRename, cancelFolderRename)}
                                      className="flex-1 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm"
                                      placeholder="Folder name"
                                    />
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        submitFolderRename();
                                      }}
                                      disabled={isSavingFolderRename || !folderRenameState.value.trim()}
                                      className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1 rounded"
                                      title="Save name"
                                    >
                                      <Check size={16} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        cancelFolderRename();
                                      }}
                                      className="text-gray-500 hover:text-gray-700 p-1 rounded"
                                      title="Cancel rename"
                                    >
                                      <X size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="font-medium truncate">📁 {folder.name}</span>
                                    {folder.include_tags && (
                                      <span className="text-sm text-gray-500">
                                        ({folder.include_tags.split(',').length} tags)
                                      </span>
                                    )}
                                  </>
                                )}
                                {draggedTag && (
                                  <span className="text-sm text-blue-600 ml-2 whitespace-nowrap">Drop tag here</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {folderRenameState.id !== folder.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startFolderRename(folder);
                                  }}
                                  className="text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 p-1 rounded"
                                  title="Rename folder"
                                >
                                  <Edit3 size={16} />
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFolder(folder.id);
                                }}
                                className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 p-1 rounded"
                                title="Delete folder"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Folder Tags */}
                          {expandedFolders.has(folder.id) && folder.include_tags && (
                            <div className="pl-4 pr-4 pb-4 bg-gray-50 border-l-4 border-blue-200">
                              <div className="space-y-2">
                                {folder.include_tags.split(',').map(tag => {
                                  const trimmedTag = tag.trim();
                                  return (
                                    <div
                                      key={trimmedTag}
                                      className={`flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border-2 border-transparent hover:border-blue-300 transition-all group ${tagRenameState.tag === trimmedTag ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'} ${draggedTag === trimmedTag ? 'opacity-50 bg-blue-200 border-blue-400' : ''}`}
                                      draggable={tagRenameState.tag !== trimmedTag}
                                      onDragStart={(e) => {
                                        if (tagRenameState.tag === trimmedTag) {
                                          e.preventDefault();
                                          return;
                                        }
                                        e.stopPropagation();
                                        setDraggedTag(trimmedTag);
                                        setDragSourceFolder(folder.id);
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', trimmedTag);
                                      }}
                                      onDragEnd={() => {
                                        setTimeout(() => {
                                          setDraggedTag(null);
                                          setDragSourceFolder(null);
                                        }, 100);
                                      }}
                                    >
                                      <div className={`flex items-center gap-2 ${tagRenameState.tag === trimmedTag ? 'flex-1' : ''}`}>
                                        <span className="text-gray-400">⋮⋮</span>
                                        {tagRenameState.tag === trimmedTag ? (
                                          <input
                                            type="text"
                                            value={tagRenameState.value}
                                            autoFocus
                                            onChange={(e) => setTagRenameState(prev => ({
                                              ...prev,
                                              value: e.target.value
                                            }))}
                                            onKeyDown={(e) => handleRenameInputKeyDown(e, submitTagRename, cancelTagRename)}
                                            className="flex-1 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm"
                                            placeholder="Tag name"
                                          />
                                        ) : (
                                          <span className="text-blue-800 font-medium select-none">
                                            🏷️ {trimmedTag}
                                          </span>
                                        )}
                                      </div>
                                      <div className={`flex items-center gap-1 ${tagRenameState.tag === trimmedTag ? '' : 'opacity-0 group-hover:opacity-100'}`}>
                                        {tagRenameState.tag === trimmedTag ? (
                                          <>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                submitTagRename();
                                              }}
                                              disabled={isSavingTagRename || !tagRenameState.value.trim()}
                                              className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1 rounded"
                                              title="Save tag name"
                                            >
                                              <Check size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                cancelTagRename();
                                              }}
                                              className="text-gray-500 hover:text-gray-700 p-1 rounded"
                                              title="Cancel rename"
                                            >
                                              <X size={16} />
                                            </button>
                                          </>
                                        ) : (
                                          <>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                startTagRename(trimmedTag);
                                              }}
                                              className="text-gray-500 hover:text-blue-600 p-1 rounded"
                                              title="Rename tag"
                                            >
                                              <Edit3 size={16} />
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const currentTags = folder.include_tags.split(',').map(t => t.trim());
                                                const newTags = currentTags.filter(t => t !== trimmedTag);
                                                updateFolderTags(folder.id, newTags);
                                              }}
                                              className="text-orange-500 hover:text-orange-700 p-1 rounded"
                                              title="Remove from folder"
                                            >
                                              <X size={16} />
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add New Folder */}
                  <div className="mt-4 flex gap-2">
                    <input
                      type="text"
                      placeholder="New folder name"
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newFolderName.trim()) {
                          createFolder(newFolderName.trim());
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        if (newFolderName.trim()) {
                          createFolder(newFolderName.trim());
                        }
                      }}
                      disabled={!newFolderName.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400"
                    >
                      Add Folder
                    </button>
                  </div>
                </div>

                {/* Right Column - Unorganized Tags */}
                <div className="flex flex-col h-full">
                  <h3 className="text-lg font-medium mb-4">📂 Unorganized Tags</h3>
                  <div
                    className={`flex-1 border rounded-lg overflow-hidden min-h-0 ${draggedTag && dragSourceFolder ? 'border-orange-400 bg-orange-50' : ''}`}
                    onDragOver={(e) => {
                      if (draggedTag && dragSourceFolder) {
                        e.preventDefault();
                      }
                    }}
                    onDrop={(e) => {
                      if (draggedTag && dragSourceFolder) {
                        e.preventDefault();

                        // Remove tag from source folder (make it unorganized)
                        const sourceFolder = contactFolders.find(f => f.id === dragSourceFolder);
                        if (sourceFolder && sourceFolder.include_tags) {
                          const sourceTags = sourceFolder.include_tags.split(',').map(t => t.trim()).filter(t => t !== draggedTag);
                          updateFolderTags(dragSourceFolder, sourceTags);
                        }

                        setDraggedTag(null);
                        setDragSourceFolder(null);
                      }
                    }}
                  >
                    <div className="h-full overflow-y-auto p-4 bg-gray-50 max-h-[50vh]">
                      {draggedTag && dragSourceFolder && (
                        <div className="mb-4 p-3 bg-orange-100 rounded-lg text-sm text-orange-700">
                          💡 Drop tags here to remove them from folders
                        </div>
                      )}
                      <div className="space-y-2">
                        {getUnorganizedTags().map(tag => (
                          <div
                            key={tag}
                            className={`flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded-lg border-2 border-transparent hover:border-gray-300 transition-all group ${tagRenameState.tag === tag ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'} ${draggedTag === tag ? 'opacity-50 bg-gray-200 border-gray-400' : ''}`}
                            draggable={tagRenameState.tag !== tag}
                            onDragStart={(e) => {
                              if (tagRenameState.tag === tag) {
                                e.preventDefault();
                                return;
                              }
                              e.stopPropagation();
                              setDraggedTag(tag);
                              setDragSourceFolder(null);
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', tag);
                            }}
                            onDragEnd={() => {
                              setTimeout(() => {
                                setDraggedTag(null);
                                setDragSourceFolder(null);
                              }, 100);
                            }}
                          >
                            <div className={`flex items-center gap-2 ${tagRenameState.tag === tag ? 'flex-1' : ''}`}>
                              <span className="text-gray-400">⋮⋮</span>
                              {tagRenameState.tag === tag ? (
                                <input
                                  type="text"
                                  value={tagRenameState.value}
                                  autoFocus
                                  onChange={(e) => setTagRenameState(prev => ({
                                    ...prev,
                                    value: e.target.value
                                  }))}
                                  onKeyDown={(e) => handleRenameInputKeyDown(e, submitTagRename, cancelTagRename)}
                                  className="flex-1 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm"
                                  placeholder="Tag name"
                                />
                              ) : (
                                <span className="text-gray-700 font-medium select-none">
                                  🏷️ {tag}
                                </span>
                              )}
                            </div>
                            <div className={`flex items-center gap-1 ${tagRenameState.tag === tag ? '' : 'opacity-0 group-hover:opacity-100'}`}>
                              {tagRenameState.tag === tag ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      submitTagRename();
                                    }}
                                    disabled={isSavingTagRename || !tagRenameState.value.trim()}
                                    className="text-green-600 hover:text-green-700 disabled:opacity-50 p-1 rounded"
                                    title="Save tag name"
                                  >
                                    <Check size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      cancelTagRename();
                                    }}
                                    className="text-gray-500 hover:text-gray-700 p-1 rounded"
                                    title="Cancel rename"
                                  >
                                    <X size={16} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startTagRename(tag);
                                    }}
                                    className="text-gray-500 hover:text-blue-600 p-1 rounded"
                                    title="Rename tag"
                                  >
                                    <Edit3 size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleTagDelete(tag);
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1 rounded"
                                    title="Delete tag globally"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TagFolderManager;
